import Component, { mixins } from "vue-class-component";
import { Watch } from "vue-property-decorator";
import { Call, CallCreateResponse, CallStatus } from "@scrile/api-provider/dist/api/CallsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { WebRTCConnection } from "@scrile/api-provider/dist/api/WebRTCProvider";
import { StreamingHelper } from "@scrile/streaming-client";
import WithAuthUser from "@/mixins/WithAuthUser";
import WithDoNotDisturb from "@/mixins/WithDoNotDisturb";
import providers from "@/lib/api";
import AudioPlayer from "@/lib/audioPlayer";
import { isMobile } from "@/lib/browserUtils";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import CallsLogger, { LoggerContextWithMessage } from "@/modules/callsLogger";
import { connectionStateEnum, EVENTS } from "@/const";
import CallModalView from "./components/CallModalView.vue";
import CallNotification from "./components/CallNotification";

const ERROR_DISPLAY_TIME = 6000;
const DEFAULT_STATS_RECORDS = 20;

type ProducersUpdatedData = {
  video: boolean;
  audio: boolean;
  screen: boolean;
};

@Component({
  name: "CallController",
  components: { CallModalView, CallNotification },
})
export default class CallController extends mixins(WithDoNotDisturb, WithAuthUser) {
  public WebRTC!: StreamingHelper;

  public call: Call | null = null;

  public callConnection: WebRTCConnection | null = null;

  public opponentUser: UserPublicData | null = null;

  public showNotification: boolean = false;

  public showConnectionError: boolean = false;

  public connectionErrorMessage: string = "";

  public errorTimeout: number | NodeJS.Timeout = 0;

  public opponentSubscriptionStatus: "follower" | "subscriber" | "user" = "user";

  public opponentStream: MediaStream = new MediaStream([]);

  public localStream: MediaStream = new MediaStream([]);

  public localReadyVideoState = false;

  public opponentReadyVideoState = false;

  public localVideoState = false;

  public opponentVideoState = false;

  public loading = false;

  public connectingState = connectionStateEnum.EMPTY;

  public isAccepted = false;

  @Watch("call")
  public watchCall(call: Call | null) {
    if (call?.status === CallStatus.NEW && call?.callerId === this.authUserStrict.id) {
      try {
        AudioPlayer.play("beep", { loop: true });
      } catch (error) {
        this.collectErrorLogs("Watch call", error as Error);
        // eslint-disable-next-line no-console
        console.warn(error);
      }
    } else {
      AudioPlayer.stop("beep");
    }
  }

  public async getStream(video: MediaStreamConstraints["video"], audio: MediaStreamConstraints["audio"]) {
    try {
      return await this.WebRTC.getStream({ video, audio });
    } catch (error) {
      this.collectErrorLogs("Getting stream", error as Error);
      this.connectionErrorMessage = video
        ? $t("Please allow the site access to the camera in your Browser settings menu.")
        : $t("Please allow the site access to the camera and microphone in your Browser settings menu.");
      throw error;
    }
  }

  public async getDevicePermission({ video, audio }: MediaStreamConstraints) {
    (await this.getStream(video, audio)).getTracks().forEach(track => track.stop());
  }

  public onLocalStreamChanged() {
    this.localVideoState = this.localStream.getVideoTracks().length > 0;
  }

  public onOpponentStreamChanged() {
    this.opponentVideoState = this.opponentStream.getVideoTracks().length > 0;
  }

  public async produceLocalStream(
    connection: WebRTCConnection,
    { video, audio }: MediaStreamConstraints,
    shouldCreateStream = true
  ) {
    this.localStream.removeEventListener("addtrack", this.onLocalStreamChanged);
    this.localStream.removeEventListener("removetrack", this.onLocalStreamChanged);
    this.localStream = await this.WebRTC.produceStream(
      connection,
      shouldCreateStream ? await this.getStream(video, audio) : { video, audio }
    );
    this.onLocalStreamChanged();
    this.localStream.addEventListener("addtrack", this.onLocalStreamChanged);
    this.localStream.addEventListener("removetrack", this.onLocalStreamChanged);
  }

  public async consumeOpponentStream(
    connection: WebRTCConnection,
    { video, audio }: { video: boolean; audio: boolean }
  ) {
    this.opponentStream.removeEventListener("addtrack", this.onOpponentStreamChanged);
    this.opponentStream.removeEventListener("removetrack", this.onOpponentStreamChanged);
    //this.opponentStream is not reactivity
    this.opponentStream = await this.WebRTC.consumeStream({
      connection,
      tracks: { video, audio },
      onData: this.changeOpponentVideoState,
    });
    this.onOpponentStreamChanged();
    this.opponentStream.addEventListener("addtrack", this.onOpponentStreamChanged);
    this.opponentStream.addEventListener("removetrack", this.onOpponentStreamChanged);
  }

  public async setVideoState(status: boolean) {
    if (!this.callConnection) {
      return;
    }

    this.loading = true;
    if (!status) {
      this.opponentReadyVideoState = status;
    }
    this.localReadyVideoState = status;
    this.WebRTC.webRTCData?.producer.client?.sendData({ video: status });

    try {
      this.businessLogicLog("Set video", { video: status, audio: true });
      await this.produceLocalStream(this.callConnection, { video: status, audio: true });
    } catch (error) {
      this.collectErrorLogs("Set video state", error as Error, { video: status, audio: true });
      this.WebRTC.webRTCData?.producer.client?.sendData({ videoState: false });
      // eslint-disable-next-line no-console
      console.warn(error);
    } finally {
      this.loading = false;
    }
  }

  public async callCreate({ receiverId }: { receiverId: string }) {
    this.doNotDisturbSet(true);
    try {
      providers.CallsProvider.setReceiveErrors(false);
      this.businessLogicLog("Call created");
      return await providers.CallsProvider.create({ receiverId });
    } catch (error) {
      this.collectErrorLogs("Call created", error as Error);
      this.doNotDisturbSet(false);
      throw error;
    }
  }

  public async callAccept(callId: string) {
    this.doNotDisturbSet(true);
    try {
      this.loading = true;
      providers.CallsProvider.setReceiveErrors(false);
      this.isAccepted = true;
      this.businessLogicLog("Call acceptation");
      return await providers.CallsProvider.accept({ callId });
    } catch (error) {
      this.collectErrorLogs("Call acceptation", error as Error);
      this.doNotDisturbSet(true);
      throw error;
    } finally {
      this.loading = false;
    }
  }

  public callEnd(callId: string) {
    this.doNotDisturbSet(false);
    providers.CallsProvider.setReceiveErrors(false);
    this.businessLogicLog("Call end");
    return providers.CallsProvider.end({ callId });
  }

  public async callOutgoingHandler({ opponentUser }: { opponentUser: UserPublicData }) {
    this.opponentUser = opponentUser;
    try {
      this.loading = true;
      await this.getDevicePermission({ audio: true });
      const { call, connection } = await this.callCreate({ receiverId: opponentUser.id });
      this.call = call;
      this.callConnection = connection;
      const context = { call: this.call, connection: this.callConnection };
      this.businessLogicLog("Setup outgoing call");
      CallsLogger.addBreadcrumb("Setup outgoing call", context);

      await this.produceLocalStream(this.callConnection, { video: false, audio: true });
    } catch (error) {
      this.collectErrorLogs("callOutgoingHandler", error as Error);
      await this.connectionErrorCb();
    } finally {
      this.loading = false;
    }
  }

  public async callUpdateHandler(call: Call) {
    if (this.call?.id !== call.id || this.showConnectionError) return;
    this.call = call;
    if (call.status === CallStatus.ACCEPTED && this.callConnection) {
      if (!this.isAccepted && call.receiverId === this.authUserStrict.id) {
        this.businessLogicLog("Update call. Hide Notification. Call was accepted by other place");
        this.reset(false, true);
        return;
      }
      try {
        this.loading = true;
        this.businessLogicLog("Update call");
        await this.consumeOpponentStream(this.callConnection, { video: false, audio: true });
      } catch (error) {
        this.collectErrorLogs("Call updating", error as Error);
        throw error;
      } finally {
        this.loading = false;
      }
    } else {
      const ignore = call.status === CallStatus.DECLINED && call.receiverId === this.authUserStrict.id;
      this.reset(false, ignore);
    }
  }

  public async callIncomingHandler(callCreateResponse: CallCreateResponse) {
    if (!this.call && !this.doNotDisturb) {
      this.showNotification = true;
      this.opponentUser = callCreateResponse.call.caller;
      this.call = callCreateResponse.call;
      this.callConnection = callCreateResponse.connection;
      const context = { call: this.call, connection: this.callConnection };
      this.businessLogicLog("Setup incoming call");
      CallsLogger.addBreadcrumb("Setup incoming call", context);

      const subscriberResult = await providers.SubscriptionProvider.search({
        data: {
          consumerIds: [callCreateResponse.call.callerId],
          providerId: callCreateResponse.call.receiverId,
          expired: false,
        },
      });
      if (subscriberResult.result.length > 0) {
        this.opponentSubscriptionStatus = "subscriber";
      } else {
        const followerResult = await providers.FollowingsProvider.find({
          data: {
            userId: callCreateResponse.call.receiverId,
            followerId: callCreateResponse.call.callerId,
          },
        });
        if (followerResult.result.length > 0) {
          this.opponentSubscriptionStatus = "follower";
        }
      }
    }
  }

  public async handleCallAccept() {
    this.showNotification = false;
    if (this.call && this.callConnection) {
      try {
        this.loading = true;
        await this.getDevicePermission({ audio: true });
        this.call = await this.callAccept(this.call.id);
        await this.produceLocalStream(this.callConnection, { video: false, audio: true });
        await this.consumeOpponentStream(this.callConnection, { video: false, audio: true });
      } catch (error) {
        this.collectErrorLogs("Call accepting", error as Error);
        await this.connectionErrorCb();
        throw error;
      } finally {
        this.loading = false;
      }
    }
  }

  public handleCallBack() {
    this.businessLogicLog("Call back");
    const opponentUser = this.opponentUser;
    this.reset(true, true);
    emitter.emit(EVENTS.CALL_OUTGOING, { opponentUser });
  }

  public closeTracks() {
    this.opponentStream.getTracks().forEach(track => track.stop());
    this.localStream.getTracks().forEach(track => track.stop());
    this.opponentStream = new MediaStream();
    this.localStream = new MediaStream();
    this.onOpponentStreamChanged();
    this.onLocalStreamChanged();
  }

  public resetLocalState(immediate: boolean = false) {
    this.opponentReadyVideoState = false;
    this.localReadyVideoState = false;
    this.callConnection = null;
    this.showConnectionError = false;
    this.connectionErrorMessage = "";
    this.isAccepted = false;
    if (this.errorTimeout !== 0) {
      clearTimeout(this.errorTimeout as number);
      this.errorTimeout = 0;
    }
    const clear = () => {
      this.call = null;
      this.opponentUser = null;
      this.showNotification = false;
      this.connectingState = connectionStateEnum.EMPTY;
    };
    if (immediate) clear();
    else setTimeout(() => clear(), 2000);
  }

  public async reset(immediate: boolean = false, ignore: boolean = false) {
    if (!ignore && this.call) this.call = await this.callEnd(this.call.id);
    this.closeTracks();
    this.WebRTC.closeAllStreams();
    this.resetLocalState(immediate);
  }

  private getWebRTCLoggingContext(maxStatsRecords: number) {
    return {
      consumer: {
        roomId: (this.WebRTC.webRTCData?.consumer.client as any)?.roomId,
        userId: (this.WebRTC.webRTCData?.consumer.client as any)?.userId,
      },
      producer: {
        roomId: (this.WebRTC.webRTCData?.producer.client as any)?.roomId,
        userId: (this.WebRTC.webRTCData?.producer.client as any)?.userId,
      },
      stats: this.WebRTC.getStatisticHistory(maxStatsRecords),
      info: {
        callId: this.call?.id,
        time: new Date().getTime(),
      },
    };
  }

  public async connectionErrorCb() {
    this.showConnectionError = true;
    this.errorTimeout = setTimeout(() => {
      this.resetLocalState();
    }, ERROR_DISPLAY_TIME);
    this.closeTracks();
    this.WebRTC.closeAllStreams();
    if (this.call) this.call = await this.callEnd(this.call.id);
  }

  public async changeOpponentVideoState({ video }: { video: ProducersUpdatedData["video"] }) {
    this.opponentReadyVideoState = video;
    try {
      if (this.callConnection) {
        await this.consumeOpponentStream(this.callConnection, { video: this.opponentReadyVideoState, audio: true });
      }
      if (!video && this.localReadyVideoState) {
        await this.setVideoState(false);
      }
    } catch (error) {
      this.collectErrorLogs("Change opponent video state", error as Error);
      throw error;
    }
  }

  public async onChangeDevice(trackData: MediaTrackConstraints) {
    this.businessLogicLog("Change device");
    if (!this.callConnection) {
      return;
    }
    if (isMobile()) {
      this.localStream.getVideoTracks().forEach(t => {
        t.stop();
        this.localStream.removeTrack(t);
      });
    }

    try {
      this.loading = true;
      await this.produceLocalStream(this.callConnection, { video: trackData, audio: true }, false);
    } catch (error) {
      this.collectErrorLogs("Change device", error as Error);
    } finally {
      this.loading = false;
    }
  }

  private collectErrorLogs(eventName: string, error: Error, context?: Record<string, any>): void {
    CallsLogger.collectLogs(`[CAll CONTROLLER]:ERROR - ${eventName}`, {
      ...this.getWebRTCLoggingContext(DEFAULT_STATS_RECORDS),
      ...context,
      streaming: true,
      error,
    });
  }

  private businessLogicLog(logMessage: string, context?: Record<string, any>): void {
    CallsLogger.collectLogs(`[CAll CONTROLLER] - ${logMessage}`, {
      call: this.call,
      connection: this.callConnection,
      ...this.getWebRTCLoggingContext(DEFAULT_STATS_RECORDS),
      ...context,
      streaming: true,
    });
  }

  public created() {
    this.WebRTC = new StreamingHelper({
      consumerOnAllStreamClosed: () => {
        this.collectErrorLogs("Consumer all stream closed", new Error("All stream closed"));
        this.connectionErrorCb();
      },
    });
    this.WebRTC.on("close", e => {
      const error = (e.data as Error | undefined) ?? new Error("Client closed");
      this.collectErrorLogs("WebRTC Event close", error, { type: e.type });
    });
    this.WebRTC.on("producers-updated", this.changeOpponentVideoState);
    emitter.on<any>(EVENTS.CALL_OUTGOING, this.callOutgoingHandler);
    this.WebRTC.logger.on("logging", ({ message, ...others }: LoggerContextWithMessage) => {
      CallsLogger.collectLogs(`[STREAM CLIENT] ${message}`, { streaming: true, ...others });
    });
    this.WebRTC.on("connectionstatechange", (state: connectionStateEnum) => {
      if (this.call?.status === CallStatus.NEW) return;
      this.connectingState = state;
    });

    window.addEventListener("beforeunload", () => {
      if (this.call && (this.call.status === CallStatus.ACCEPTED || this.call.callerId === this.authUserStrict.id)) {
        this.businessLogicLog("window was closed");
        this.reset();
      }
      return "";
    });

    this.loading = true;
    Promise.all([
      providers.CallsProvider.subscribeIncomingCall(this.callIncomingHandler),
      providers.CallsProvider.subscribeCallStatusUpdated(this.callUpdateHandler),
    ])
      .then(([subscriptionIncomingCall, subscriptionStatusUpdate]) => {
        this.$on("hook:beforeDestroy", () => {
          if (subscriptionIncomingCall) subscriptionIncomingCall.unsubscribe();
          if (subscriptionStatusUpdate) subscriptionStatusUpdate.unsubscribe();
          emitter.off<any>(EVENTS.CALL_OUTGOING, this.callOutgoingHandler);
        });
      })
      .catch(error => {
        this.collectErrorLogs("Created get initial data", error);
      })
      .finally(() => (this.loading = true));
  }
}
